import React from "react"
import { faChartLine, faSackDollar, faShop } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { AdminDashboard } from "../../../Objects/AdminDashboard/AdminDashboard"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { SeriesChart } from "../components/SeriesChart"

interface CommercialPerformanceSectionProps {
  dashboard: AdminDashboard
  originSeries: { label: string; value: number }[]
  originRevenueSeries: { label: string; value: number }[]
}

export const CommercialPerformanceSection: React.FC<CommercialPerformanceSectionProps> = ({
  dashboard,
  originSeries,
  originRevenueSeries,
}) => {
  const { t } = useTranslation("frontend")

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <div className="mt-2 grid grid-cols-1 gap-4 p-1 lg:grid-cols-3">
        <AdminMetricCard
          title={t("admin_dashboard.commercial_total_subscriptions")}
          value={`${dashboard.commercialPerformance.summary.totalSubscriptions}`}
          icon={faChartLine}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.commercial_top_origin")}
          value={`${dashboard.commercialPerformance.summary.topOriginByConversion.toECommercialOriginTypeString()} (${dashboard.commercialPerformance.summary.topOriginSubscriptionsCount})`}
          icon={faShop}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.commercial_monthly_recurring_revenue")}
          value={convertToLocalMoney(dashboard.commercialPerformance.summary.totalMonthlyRecurringRevenue)}
          icon={faSackDollar}
          tone="positive"
        />
      </div>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
        <div>
          <div className="mt-4 grid grid-cols-1 gap-4 p-1 2xl:grid-cols-2">
            <SeriesChart title={t("admin_dashboard.commercial_conversion_by_origin")} data={originSeries} />

            <SeriesChart
              title={t("admin_dashboard.commercial_revenue_by_origin")}
              data={originRevenueSeries}
              money={true}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 p-1">
            <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">
                {t("admin_dashboard.commercial_table_by_origin")}
              </h3>

              {dashboard.commercialPerformance.origins.length === 0 ? (
                <p className="mt-3 text-xs text-slate-500">{t("admin_dashboard.commercial_empty_origins")}</p>
              ) : (
                <div className="mt-3 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b text-center text-slate-500">
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_origin")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_subscriptions")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_percentage")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_active")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_cancelled")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_revenue")}</th>
                        <th className="py-2 pr-2">{t("admin_dashboard.commercial_mrr")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.commercialPerformance.origins.map((item) => (
                        <tr key={item.originType} className="border-b last:border-0 text-center">
                          <td className="py-2 pr-2 font-semibold text-slate-700">
                            {item.originType.toECommercialOriginTypeString()}
                          </td>
                          <td className="py-2 pr-2">{item.subscriptionsCount}</td>
                          <td className="py-2 pr-2">{item.subscriptionsPercentage.toFixed(2)}%</td>
                          <td className="py-2 pr-2">{item.activeSubscriptionsCount}</td>
                          <td className="py-2 pr-2">{item.cancelledSubscriptionsCount}</td>
                          <td className="py-2 pr-2">{convertToLocalMoney(item.totalRevenue)}</td>
                          <td className="py-2 pr-2">{convertToLocalMoney(item.monthlyRecurringRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Animate>
    </div>
  )
}
